<template>
  <div class="profile-tree">

    <b-row
      class="profile-tree__row flex-column-reverse flex-sm-row"
      no-gutters
    >
      <b-col
        cols="12"
        sm="8"
      >
        <div
          class="profile-tree__tree-panel"
        >
          <div class="profile-tree__tree-label text-red">
            {{ $t('profile-manager.query.hierarchical-presentation') }}
            <i
              aria-hidden="true"
              class="fas fa-info-circle profile-tree__selection-tip"
              id="profile-tree__selection-tip"
            />
            <b-tooltip
              target="profile-tree__selection-tip"
              placement="right"
            >
              <span v-html="$t('profile-manager.query.hierarchical-selection-tip')"></span>
            </b-tooltip>
          </div>
          <v-tree
            v-if="!loading && profiles.length"
            class="profile-tree__tree-view"
            ref="tree"
            :data="profiles"
            multiple
            scoped
            select-alone
            @node-click="selectNode"
            @node-check="selectCheck($event, $event.checked)"
          />
          <s-empty-list
            v-else
            :loading="loading"
          />
        </div>
      </b-col>
      <b-col
        cols="12"
        sm="4"
      >
        <div
          class="d-flex flex-column justify-content-between h-100 profile-tree__exterior-panel"
        >
          <div
            class="profile-tree__top-filter-and-control"
          >
            <div
              class="profile-tree__side-panel"
            >
              <tree-filter-and-control
                v-model="searchTerm"
                @input="changingSearch()"
                @expand-all="expandAll()"
                @collapse-all="collapseAll()"
              />
            </div>
            <div
              class="profile-tree__side-filter-and-control"
            >
              <tree-filter-and-control
                v-model="searchTerm"
                @input="changingSearch()"
                @expand-all="expandAll()"
                @collapse-all="collapseAll()"
              />
            </div>
            <div class="pb-5">
              <b-button
                @click="showLinkedUsers()"
                variant="outline-primary"
                class="profile-tree__show-users btn-block"
                :disabled="!selectedProfiles.length"
              >
                {{
                  $tc('profile-manager.query.show-selected-profiles-linked-users',
                    selectedProfiles.length,
                    {count: selectedProfiles.length})
                }}
              </b-button>
            </div>
              <div
                v-show="selectedProfiles.length"
                class="profile-tree__selected-count text-red"
              >
                <span class="fa fa-exclamation-triangle profile-tree__icon"></span>
                {{
                  $tc('profile-manager.query.there-are-profiles-selected',
                    selectedProfiles.length,
                    {count: selectedProfiles.length})
                }}
              </div>
              <b-button
                @click="deleteSelected()"
                variant="primary"
                class="profile-tree__delete-button btn-block"
                :disabled="!selectedProfiles.length || cannotRemoveProfiles"
              >
                <span class="far fa-trash-alt profile-tree__icon"></span>
                {{
                  $tc('profile-manager.query.delete-selected-profiles',
                    selectedProfiles.length,
                    {count: selectedProfiles.length})
                }}
              </b-button>
          </div>
          <div class="w100">
            <b-button
              @click="triggerBackToList()"
              class="profile-tree__back-button btn-block"
            >
              {{ $t('profile-manager.query.back-to-list-view') }}
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import VTree from 'vue-tree-halower';
import { delayed } from '@/services/observables';
import loadingMixin from '@/services/loadingMixin';
import Service from '@/domain/services/generalPanel/adminPanel/profile/profileService';
import TreeFilterAndControl from './TreeFilterAndControl';

function forEachProfile(fn) {
  const iterator = (profile) => {
    fn(profile);
    if (profile.children) {
      profile.children.forEach(iterator);
    }
  };
  return iterator;
}

export default {
  name: 'Tree',
  components: {
    TreeFilterAndControl,
    VTree,
  },
  mixins: [
    loadingMixin,
  ],
  data: vm => ({
    doDelayedRequest: delayed((...args) => vm.doRequest(...args)),
    searchTerm: null,
    selectedProfiles: [],
    profiles: [],
    treeProfiles: [],
  }),
  computed: {
    cannotRemoveProfiles() {
      return this.selectedProfiles
        ? this.selectedProfiles.filter(profile => !profile.canRemove).length > 0 : false;
    },
  },
  created() {
    this.refreshItems();
  },
  methods: {
    changingSearch() {
      this.refreshItems(true);
    },
    triggerBackToList() {
      this.$emit('back-to-list');
    },
    selectChange() {
      this.$nextTick(() => {
        this.selectedProfiles = this.$refs.tree
          ? this.$refs.tree.getCheckedNodes()
          : [];
      });
    },
    selectNode(item) {
      this.$nextTick(() => {
        this.$set(item, 'checked', item.checked ? false : item.selected);
        this.$set(item, 'selected', item.checked);
        this.selectChange();
      });
    },
    selectAllChildren(node, checked) {
      this.$set(node, 'selected', checked);
      if (node.children.length) {
        this.$set(node, 'checked', checked);
        const mappedChildren = node.children.map((item) => {
          this.selectAllChildren(item, checked);
          return { ...item, checked };
        });
        this.$set(node, 'children', mappedChildren);
      }
      this.selectChange();
    },
    selectCheck(item, checked) {
      this.$nextTick(() => {
        this.selectAllChildren(item, checked);
      });
    },
    deleteSelected() {
      this.$emit('delete-profiles', {
        ids: this.selectedProfiles.map(p => p.id),
        callback: this.refreshItems,
      });
    },
    showLinkedUsers() {
      this.$emit('show-linked-users', {
        profilesIds: this.selectedProfiles.map(p => p.id),
        filterOnlySelectedProfile: false,
      });
    },
    expandAll() {
      this.forEachProfile((profile) => {
        // eslint-disable-next-line no-param-reassign
        profile.expanded = true;
      });
    },
    collapseAll() {
      this.forEachProfile((profile) => {
        // eslint-disable-next-line no-param-reassign
        profile.expanded = false;
      });
    },
    forEachProfile(fn) {
      this.profiles.forEach(forEachProfile(fn));
      this.setProfiles(this.profiles);
    },
    refreshItems(withDelay) {
      this.startLoading();
      this.selectedProfiles = [];

      const execRequest = withDelay ? this.doDelayedRequest : this.doRequest;

      return execRequest(this.getRequestFilter())
        .finally(() => {
          this.endLoading();
        });
    },
    doRequest(requestFilter) {
      this.profiles = [];
      this.startLoading();
      return Service
        .getProfilesTree(requestFilter)
        .then((profiles) => {
          this.setProfiles(profiles);
          this.expandAll();
        })
        .finally(() => {
          this.endLoading();
        });
    },
    getRequestFilter() {
      return {
        name: this.searchTerm,
      };
    },
    setProfiles(profiles) {
      this.profiles = cloneDeep(profiles);
    },
  },
};
</script>

<style src="vue-tree-halower/dist/halower-tree.min.css"></style>
<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.profile-tree
  display: flex
  flex-direction: column

  &__selection-tip
    color: $color-link

  &__exterior-panel
    padding: 0 15px

  &__tree-panel
    flex: 1
    overflow: auto
    max-height: 500px

  &__tree-label
    font-weight: bold
    padding: 20px

  &__side-panel
    flex: 1
    display: flex
    flex-direction: column
    padding: 20px

    > *
      margin-bottom: 30px

  &__selected-count
    margin-bottom: 5px
    align-self: flex-end

  &__show-users
    white-space: normal
    font-weight: bold

  &__icon
    margin-right: 5px

  &__top-filter-and-control
    padding: 0 5px
    display: block

  &__side-filter-and-control
    display: none

  ::v-deep
    .node-title:hover
      background-color: var(--color-treeview-background-hover)

    .node-selected:not(.node-selected:hover)
      border: none
      background-color: unset

    .halo-tree .inputCheck
      vertical-align: text-top

    .halo-tree .tree-expand
      vertical-align: middle
      background-color: var(--color-background-light)

::v-deep
  .halo-tree .tree-node-el
    background-color: transparent

  +media-min-sm()
    flex-direction: row
    &__tree-panel
      flex: 2
    &__side-panel
      flex: 1
      display: flex
      max-width: 350px
    &__back-button
      margin-top: 60px
      font-weight: bold
    &__top-filter-and-control
      display: none
    &__side-filter-and-control
      display: block
  +media-min-md()
  +media-min-lg()
</style>
